import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { curProductAtom, hasCmbChinaToPaidAtom, seatsAtom, visibleCmbChinaAtom } from '@/store';
import Modal from '../modal';
import Loading from '@/components/loading';
import { getUrlParam, parseQueryString, showToast } from '@/utils';
import { closeVipSubscribeDialog } from '@/utils/vip';
import IconFont from '@/components/icon-font';
import classnames from 'classnames';
import { AccountType, TradePurchaseScene } from '@meitu/subscribe-core';
import { initSubscribeSdk } from '@/utils/subscribe';
import { purchaseScene, PurchaseScene } from '@/constants';
import { fetchPayContentInfo } from '@/services/subscription';
import { CmbChina } from '@/services/subscription/index.d';
import dayjs from 'dayjs';

import styles from './index.module.css';
import { track } from '@/utils/common';
import { useLocaleStore } from '@/i18n/localeStore';

interface IProps {
  transferId: string;
}
const CmbChinaModal: React.FC<IProps> = (props) => {
  const { $t } = useLocaleStore();
  const { transferId } = props;

  const [visibleCmbChina, setVisibleCmbChina] = useAtom(visibleCmbChinaAtom);
  const [, setHasCmbChinaToPaid] = useAtom(hasCmbChinaToPaidAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [seats] = useAtom(seatsAtom);

  const [loading, setLoading] = useState<boolean>(false);
  // 关闭提醒弹窗
  const [visible, setVisible] = useState<boolean>(false);
  // 取消订单二次确认弹窗
  const [visibleCancelConfirm, setVisibleCancelConfirm] = useState<boolean>(false);
  // 存在待支付订单提示弹窗
  const [visibleHasOrder, setVisibleHasOrder] = useState<boolean>(false);
  const [payContent, setPayContent] = useState<CmbChina>();
  const [orderId, setOrderId] = useState<string>('');
  const [orderInfo, setOrderInfo] = useState<{
    amount?: number;
    symbol?: string;
    valid_time?: string;
  }>();
  const eleRef = useRef<HTMLDivElement>(null);
  const instance = useRef<any>();

  const handleClose = () => {
    setVisibleCmbChina(false);

    if (payContent) {
      setVisible(true);
    }

    track({
      event_id: 'corporate_payment_pop_click',
      event_name: '对攻转账流程弹窗点击	',
      params: {
        pop_type: 'payment_message',
        class: 'close',
      },
    });
  };

  const handleCopy = () => {
    if (!payContent) return;

    const text = `账户名称： ${payContent.account_name}\n银行账户： ${payContent.bank_card_no}\n开户银行： ${payContent.issue_bank}\n汇款码： ${payContent.bank_number}`;
    navigator.clipboard.writeText(text);
    showToast(
      $t((v) => v.Message_CopiedToClipboard),
      eleRef.current
    );

    track({
      event_id: 'corporate_payment_pop_click',
      event_name: '对攻转账流程弹窗点击	',
      params: {
        pop_type: 'payment_message',
        class: 'copy',
      },
    });
  };

  const handleJump = (pop_type: 'already' | 'confirm_cancel' | 'payment_message') => {
    track({
      event_id: 'corporate_payment_pop_click',
      event_name: '对攻转账流程弹窗点击	',
      params: {
        pop_type,
        class: 'view',
      },
    });

    const pathName = window.parent.location.pathname;

    if (pathName !== '/admin/order') {
      // 如果是在非订单发票页面，则跳转到订单发票页面
      const env = process.env.REACT_APP_ENV;
      const prefix = env === 'release' ? '' : `${env}.`;
      setTimeout(() => {
        window.parent.location.href = `https://${prefix}designkit.cn/admin/order`;
      }, 300);
    } else {
      setTimeout(() => {
        handleCloseSub();
      }, 300);
    }
  };

  const handleCloseSub = () => {
    // 关闭
    closeVipSubscribeDialog({
      success: true,
    });
  };

  const handleCancel = async () => {
    track({
      event_id: 'corporate_payment_pop_click',
      event_name: '对攻转账流程弹窗点击	',
      params: {
        pop_type: 'already',
        class: 'confirm_cancel_create',
      },
    });

    try {
      setLoading(true);

      const res = await instance?.current.request(
        `${process.env.REACT_APP_API_SUB_HOST}/h5/order/change.json`,
        {
          method: 'post',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            group_id: getUrlParam('groupId'),
            event_type: 1,
            order_id: orderId,
          }),
        }
      );

      if (res.code === 0) {
        setVisibleCancelConfirm(false);
        setOrderId('');
        showToast($t((v) => v.Message_CancelSuccess));
        setHasCmbChinaToPaid(false);
        return;
      }

      showToast($t((v) => v.Message_CancelFailed));
    } catch (e) {
      showToast($t((v) => v.Message_CancelFailed));
    } finally {
      setLoading(false);
    }
  };

  // 请求是否存在待支付的对公转账订单
  const checkHasOrderToPaid = async () => {
    try {
      const res = await instance?.current.request(
        `${process.env.REACT_APP_API_SUB_HOST}/h5/transaction/last_order/pay_content.json`,
        {
          params: {
            account_id: getUrlParam('groupId'),
            account_type: AccountType.GroupId,
            pay_channel: 'cmbchina',
            order_status: 100,
          },
        }
      );

      if (res.code === 0 && res.data) {
        const payContent = decodeURIComponent(
          JSON.parse(res.data.payContent || '{}')?.payContent || ''
        );
        // const obj = parseQueryString(payContent);

        if (payContent) {
          setVisibleHasOrder(true);
          setOrderId(res.data?.orderId || '');
          setHasCmbChinaToPaid(true);
          return true;
        }
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  // 获取对公转账银行信息
  const getPayContentInfo = async (content: string) => {
    const params: {
      [key: string]: string;
    } = {
      content,
      trade_type: 'MWEB',
      pay_channel: 'cmbchina',
    };
    const res = await fetchPayContentInfo(params);
    setPayContent(res.data.data?.cmbchina);
  };

  // 创建对公转账订单
  const handleCreateOrder = async () => {
    try {
      setLoading(true);

      // 取第一个促销优惠
      const promotionInfo = curProduct?.promotions && curProduct.promotions[0];

      const res = await instance?.current.request(
        `${process.env.REACT_APP_API_SUB_HOST}/h5/transaction/v2/create.json`,
        {
          method: 'post',
          params: {
            account_id: getUrlParam('groupId'),
            account_type: AccountType.GroupId,
            product_id: curProduct.productId,
            transfer_id: transferId, // 透传 ID
            promotion_id: promotionInfo?.promotionId || '',
            trade_session_id: curProduct.tradeSessionId,
            quantity: seats,
            purchase_scene:
              purchaseScene === PurchaseScene.EXTRA
                ? TradePurchaseScene.Extra
                : TradePurchaseScene.Normal,
            pay_channel: 'cmbchina',
            cancel_duration: 14 * 24 * 60 * 60 * 1000, // 订单取消时间， 14天
          },
        } as any
      );

      if (res.code === 0) {
        const str = decodeURIComponent(res.data.financialContent || '');
        const obj = parseQueryString(str);
        setOrderInfo({
          amount: obj.amount as number,
          symbol: obj.symbol as string,
          valid_time: res.data.cancelTime
            ? dayjs(+res.data.cancelTime).format('YYYY-MM-DD HH:mm:ss')
            : '',
        });
        await getPayContentInfo(res.data.financialContent);
      }
    } catch (e: any) {
      showToast(e?.message || $t((v) => v.Message_CreateFail), eleRef.current);
      console.log(e, 'create order error==========');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFn = async () => {
    // 创建订单前先校验是否存在待支付订单
    const status = await checkHasOrderToPaid();
    !status && handleCreateOrder();
  };

  useEffect(() => {
    instance.current = initSubscribeSdk();
    checkHasOrderToPaid();
  }, []);

  useEffect(() => {
    if (visibleCmbChina) {
      handleCreateFn();

      // 对公转账弹窗曝光
      track({
        event_id: 'corporate_payment_pop_show',
        event_name: '对攻转账流程弹窗曝光',
        params: {
          pop_type: 'payment_message',
        },
      });
    }
  }, [visibleCmbChina]);

  useEffect(() => {
    if (visibleHasOrder) {
      track({
        event_id: 'corporate_payment_pop_show',
        event_name: '对攻转账流程弹窗曝光',
        params: {
          pop_type: 'already',
        },
      });
    }
  }, [visibleHasOrder]);

  useEffect(() => {
    if (visibleCancelConfirm) {
      track({
        event_id: 'corporate_payment_pop_show',
        event_name: '对攻转账流程弹窗曝光',
        params: {
          pop_type: 'confirm_cancel',
        },
      });
    }
  }, [visibleCancelConfirm]);

  return (
    <>
      {/* 对公转账信息弹窗 */}
      <Modal
        visible={visibleCmbChina}
        onClose={handleClose}
        style={{
          backgroundColor: 'var(--background-secondary-activated)',
          width: '500px',
          height: 'auto',
        }}
      >
        <div className={styles.body} ref={eleRef}>
          <div className={styles.top}>
            <div className={styles.title}>
              {$t((v) => v.Title_TransferAmount)}
              <span className={styles.amount}>
                {orderInfo ? `${orderInfo.symbol}${(orderInfo.amount || 0) / 100}` : '-'}
              </span>
            </div>
            <div className={styles.time}>
              {$t((v, { r }) => r(v.Text_PayBeforeDeadline, [orderInfo?.valid_time || '-']))}
            </div>
            <div className={styles.desc}>{$t((v) => v.Desc_TransferAmountMatch)}</div>
            <div className={styles.desc}>{$t((v) => v.Desc_TransferUniqueCode)}</div>
            <div className={styles.desc}>{$t((v) => v.Desc_TransferAutoActivate)}</div>
          </div>
          <div className={styles.bottom}>
            <div className={styles.bottomTitle}>{$t((v) => v.Title_ReceiveAccountInfo)}</div>
            <div className={styles.itemRow}>
              <span className={styles.label}>{$t((v) => v.Label_AccountName)}</span>
              <span>{payContent?.account_name || '-'}</span>
            </div>
            <div className={styles.itemRow}>
              <span className={styles.label}>{$t((v) => v.Label_BankAccount)}</span>
              <span>{payContent?.bank_card_no || '-'}</span>
            </div>
            <div className={styles.itemRow}>
              <span className={styles.label}>{$t((v) => v.Label_IssueBank)}</span>
              <span>{payContent?.issue_bank || '-'}</span>
            </div>
            <div className={styles.itemRow}>
              <span className={styles.label}>{$t((v) => v.Label_RemittanceCode)}</span>
              <span>{payContent?.bank_number || '-'}</span>
            </div>
          </div>
          <div
            className={classnames(styles.btn, !payContent && styles.disable)}
            onClick={handleCopy}
          >
            {$t((v) => v.Text_CopyRemittanceInfo)}
          </div>
        </div>
      </Modal>
      {loading ? <Loading text="" /> : null}

      {/* 关闭对公转账信息弹窗后的二次确认弹窗 */}
      <Modal
        visible={visible}
        onClose={handleCloseSub}
        style={{
          backgroundColor: 'var(--background-secondary-activated)',
          width: '300px',
          height: 'auto',
        }}
      >
        <div className={styles.confirmBody}>
          <div className={styles.title}>{$t((v) => v.Title_TransferReminder)}</div>
          <div className={styles.content}>{$t((v) => v.Text_ViewOrderProgress)}</div>
          <div className={styles.btn} onClick={() => handleJump('payment_message')}>
            {$t((v) => v.Button_GoNow)}
          </div>
        </div>
      </Modal>

      {/* 有待支付对公订单的提示弹窗 */}
      <Modal
        visible={visibleHasOrder}
        hideClose
        style={{
          backgroundColor: 'var(--background-secondary-activated)',
          width: 'auto',
          minWidth: '340px',
          height: 'auto',
        }}
      >
        <div className={styles.confirmBody}>
          <IconFont
            name="infoCircleFill"
            fontSize={70}
            color="var(--content-system-info-icon-regular)"
          />
          <div className={styles.title}>{$t((v) => v.Title_PendingTransfer)}</div>
          <div className={styles.content}>{$t((v) => v.Text_HasPendingTransfer)}</div>
          <div className={styles.btns}>
            <div
              className={classnames(styles.btn, styles.defaultBtn)}
              onClick={() => {
                setVisibleHasOrder(false);
                setVisibleCancelConfirm(true);
                track({
                  event_id: 'corporate_payment_pop_click',
                  event_name: '对攻转账流程弹窗点击	',
                  params: {
                    pop_type: 'already',
                    class: 'cancel_create',
                  },
                });
              }}
            >
              {$t((v) => v.Button_CancelAndCreate)}
            </div>
            <div className={styles.btn} onClick={() => handleJump('already')}>
              {$t((v) => v.Button_ViewTransferOrder)}
            </div>
          </div>
        </div>
      </Modal>

      {/* 取消对账转账订单二次确认弹窗 */}
      <Modal
        visible={visibleCancelConfirm}
        hideClose
        style={{
          backgroundColor: 'var(--background-secondary-activated)',
          width: 'auto',
          height: 'auto',
        }}
      >
        <div className={styles.confirmBody}>
          <IconFont
            name="infoCircleFill"
            fontSize={70}
            color="var(--content-system-info-icon-regular)"
          />
          <div className={styles.title}>{$t((v) => v.Title_ConfirmCancel)}</div>
          <div className={styles.content}>
            {$t((v) => v.Text_CancelOrderNote1)}
            <br />
            {$t((v) => v.Text_CancelOrderNote2)}
          </div>
          <div className={styles.btns}>
            <div className={classnames(styles.btn, styles.defaultBtn)} onClick={handleCancel}>
              {$t((v) => v.Button_ConfirmCancelCreate)}
            </div>
            <div className={styles.btn} onClick={() => handleJump('confirm_cancel')}>
              {$t((v) => v.Button_ViewTransferOrder)}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default React.memo(CmbChinaModal);
